import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../utils/dateUtils.ts';
import { getAssetDeclaration, AssetDeclarationReport, AssetDeclarationItem } from '../services/fiiApi.ts';
import './FIIAssetDeclaration.css';

const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_YEAR = CURRENT_YEAR - 1;

const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => CURRENT_YEAR - i).filter(y => y >= 2000);

function formatCNPJ(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (d.length !== 14) return raw;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: select textarea
    }
  };

  return (
    <button className="fii-decl-copy-btn" onClick={handleCopy}>
      {copied ? 'Copiado!' : 'Copiar'}
    </button>
  );
}

function DeclarationCard({ item, year }: { item: AssetDeclarationItem; year: number }) {
  return (
    <div className="fii-decl-card">
      <div className="fii-decl-card-header">
        <span className="fii-decl-ticker">{item.ticker}</span>
        {item.fund_name && <span className="fii-decl-fund-name">{item.fund_name}</span>}
        {item.cnpj && (
          <span className="fii-decl-cnpj-chip">{formatCNPJ(item.cnpj)}</span>
        )}
      </div>

      <div className="fii-decl-discriminacao-block">
        <label className="fii-decl-label">Discriminação</label>
        <div className="fii-decl-discriminacao-row">
          <textarea
            className="fii-decl-textarea"
            readOnly
            value={item.discriminacao}
            rows={3}
          />
          <CopyButton text={item.discriminacao} />
        </div>
      </div>

      <div className="fii-decl-positions">
        <div className="fii-decl-position">
          <h4>Situação em 31/12/{year - 1}</h4>
          <dl>
            <dt>Quantidade</dt>
            <dd>{item.previous_year_quantity}</dd>
            <dt>Preço médio</dt>
            <dd>{formatCurrency(item.previous_year_average_price)}</dd>
            <dt>Total</dt>
            <dd>{formatCurrency(item.previous_year_total)}</dd>
          </dl>
        </div>
        <div className="fii-decl-position">
          <h4>Situação em 31/12/{year}</h4>
          <dl>
            <dt>Quantidade</dt>
            <dd>{item.current_year_quantity}</dd>
            <dt>Preço médio</dt>
            <dd>{item.current_year_quantity > 0 ? formatCurrency(item.current_year_average_price) : 'R$ 0,00'}</dd>
            <dt>Total</dt>
            <dd>{formatCurrency(item.current_year_total)}</dd>
          </dl>
        </div>
      </div>
    </div>
  );
}

const FIIAssetDeclaration: React.FC = () => {
  const [year, setYear] = useState(DEFAULT_YEAR);
  const [report, setReport] = useState<AssetDeclarationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isOffline = !navigator.onLine;

  const loadReport = useCallback(async (y: number) => {
    setLoading(true);
    setError('');
    try {
      const data = await getAssetDeclaration(y);
      setReport(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOffline) {
      loadReport(year);
    }
  }, [year, isOffline, loadReport]);

  if (isOffline) {
    return (
      <div className="fii-decl-page">
        <div className="fii-decl-offline">
          Esta tela requer conexão com a internet. Reconecte-se e tente novamente.
        </div>
      </div>
    );
  }

  return (
    <div className="fii-decl-page">
      <div className="fii-decl-header no-print">
        <div className="fii-decl-title-row">
          <h1>Declaração de Bens — IRPF</h1>
          <button className="fii-decl-print-btn" onClick={() => window.print()}>
            Imprimir / PDF
          </button>
        </div>
        <div className="fii-decl-controls">
          <label htmlFor="fii-decl-year">Ano-calendário:</label>
          <select
            id="fii-decl-year"
            value={year}
            onChange={e => setYear(Number(e.target.value))}
          >
            {YEAR_OPTIONS.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <p className="fii-decl-group-code">Grupo 07 · Código 03 — Fundo de Investimento Imobiliário</p>
      </div>

      <div className="fii-decl-notice no-print">
        <span className="fii-decl-notice-icon">⚠️</span>
        <span>
          Cotas vendidas geram apuração de ganho de capital e devem ser declaradas em outra ficha.{' '}
          <Link to="/fii/darf">Ver Controle DARF</Link>
        </span>
      </div>

      {loading && (
        <div className="fii-decl-skeleton">
          {[1, 2, 3].map(i => (
            <div key={i} className="fii-decl-skeleton-card" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="fii-decl-error">
          <p>{error}</p>
          <button onClick={() => loadReport(year)}>Tentar novamente</button>
        </div>
      )}

      {!loading && !error && report && report.items.length === 0 && (
        <div className="fii-decl-empty">
          Nenhum FII com posição relevante para {year}. Verifique se você possui operações registradas.
        </div>
      )}

      {!loading && !error && report && report.items.length > 0 && (
        <>
          <div className="fii-decl-cards">
            {report.items.map(item => (
              <DeclarationCard key={item.ticker} item={item} year={year} />
            ))}
          </div>

          <div className="fii-decl-footer">
            <h3>Totais consolidados</h3>
            <div className="fii-decl-totals">
              <div>
                <span>31/12/{year - 1}</span>
                <strong>{formatCurrency(report.total_previous_year)}</strong>
              </div>
              <div>
                <span>31/12/{year}</span>
                <strong>{formatCurrency(report.total_current_year)}</strong>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FIIAssetDeclaration;
